import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { useApiQuery } from "../../../services/api";

/**
 * What Chat-with-Paper will actually put in front of the agent for this paper,
 * and therefore what every surface that offers the chat is allowed to CALL it.
 *
 * The three kinds are not degrees of the same thing. `full_text` is the paper as
 * published, `abstract` is the publisher's abstract alone, and `digest` is
 * Juisci's own curated summary — not the source's words at all. An answer built
 * on the third cannot be described to an HCP as an answer about the paper.
 *
 * It comes from the server because only the server can know: the resolution
 * walks the parsing blob, then the work's crawled text, then the article's
 * summary fields, and the winner of that walk is the fact. The article payload
 * used to carry `hasFullText` / `hasAbstract` instead, counted from JSON-path
 * presence — and a key holding a JSON `null` counts as present, so `hasFullText`
 * was true for all 259 141 published articles on staging while 118 915 of them
 * hold only an abstract. Every one of those was labelled "this paper".
 *
 * `unresolved` and `none` are kept apart on purpose: the first is ignorance (in
 * flight, or the request failed), the second is the paper having no usable text
 * at all — the state in which the stream itself answers 422. Both mean "do not
 * offer the chat", but only the second is a fact about the paper.
 */
export type PaperGrounding =
  | { status: "unresolved" }
  | { status: "none" }
  | { status: "ready"; kind: "full_text" | "abstract" | "digest" };

export function usePaperGrounding(slug: string): PaperGrounding {
  const query = useApiQuery(
    "/webapp/public/articles/:slug/chat-grounding",
    slug.length > 0 ? { param: { slug } } : false
  );
  // Fail closed, like the visual-abstract chip: a surface that cannot say what
  // it is offering does not offer it. An error is not a `none` — we do not know
  // that the paper has no text, only that we failed to ask.
  if (query.state !== "success") return { status: "unresolved" };
  const { kind } = query.body;
  return kind === null ? { status: "none" } : { status: "ready", kind };
}

export type PaperGroundingKind = Extract<
  PaperGrounding,
  { status: "ready" }
>["kind"];

/**
 * What the article page may offer to chat on, or null when it offers no chat.
 * Unresolved and text-less papers offer nothing: the composer's placeholder
 * names what the agent will read, so there is no honest wording for a paper we
 * cannot describe. Open access is deliberately NOT consulted: it never changed
 * what the stream grounds on, only what the label claimed, which is how a
 * closed-access paper whose full text we hold got called "abstract".
 */
export function useArticleChatGrounding(
  slug: string
): PaperGroundingKind | null {
  const CHAT_WITH_ABSTRACT = useFeatureIsOn("CHAT_WITH_ABSTRACT");
  const grounding = usePaperGrounding(slug);
  if (grounding.status !== "ready") return null;
  if (grounding.kind !== "full_text" && !CHAT_WITH_ABSTRACT) return null;
  return grounding.kind;
}

/**
 * The name of the chat, on the surfaces that title it: the conversation header
 * and the CTA card of a shared transcript. Both draw the same word for the same
 * grounding, which is the whole point of one table.
 *
 * `t` is taken rather than read from the singleton, per `studyDesignLabel`: the
 * callers are components inside `useTranslation()` and must pass their own so a
 * language change re-renders them.
 */
export function chatGroundingTitle(
  t: (key: string) => string,
  kind: PaperGroundingKind
): string {
  switch (kind) {
    case "full_text":
      return t("Chat With Paper");
    case "abstract":
      return t("Chat with abstract");
    case "digest":
      return t("Chat with the Juisci summary");
    default:
      throw new Error(kind satisfies never);
  }
}

/** The composer's placeholder, on the article page and on the chat landing. */
export function chatGroundingPlaceholder(
  t: (key: string) => string,
  kind: PaperGroundingKind
): string {
  switch (kind) {
    case "full_text":
      return t("Ask questions about this paper");
    case "abstract":
      return t("Ask questions about this abstract");
    case "digest":
      return t("Ask questions about this Juisci summary");
    default:
      throw new Error(kind satisfies never);
  }
}
